import React, { useState, useRef, useEffect } from 'react';

import { css, keyframes } from '@emotion/react';
import { LucidTheme } from '@lucid/theme';

export type AspectRatioProps = '1:1' | '4:3' | '16:9';

export interface ImageProps {
  alt: string;
  className?: string;
  src: string;
  title?: string;
  aspectRatio?: AspectRatioProps;
  isBackgroundImage?: boolean;
}

const loadingKeyframe = keyframes({
  '0%': {
    backgroundPosition: '-100%'
  },
  '100%': {
    backgroundPosition: '100%'
  }
});

const loadingCSS = (theme: LucidTheme) => css({
  background: `linear-gradient(to right, ${theme.colors.coolGrey[5]} 10%, ${theme.colors.coolGrey[10]} 33%, ${theme.colors.coolGrey[5]} 66%)`,
  backgroundSize: '200% 100%',
  transition: 'opacity 0.25s',
  animation: `${loadingKeyframe} 2s ease infinite`,
  animationTimingFunction: 'linear'
});

const Image = ({
  alt,
  className,
  src,
  title,
  aspectRatio = '16:9',
  isBackgroundImage = false
}: ImageProps) => {
  const ref = useRef<HTMLImageElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const ratio = aspectRatio.split(':');
  const paddingRatio = (parseInt(ratio[1], 10) / parseInt(ratio[0], 10)) * 100;

  const onLoad = () => {
    setIsLoaded(true);
  };

  useEffect(() => {
    if (ref.current && ref.current.complete) {
      onLoad();
    }
  });

  return (
    <div
      css={(theme: LucidTheme) => [
        {
          position: 'relative',
          overflow: 'hidden',
        },
        !isBackgroundImage && {
          height: 0,
          paddingBottom: `${paddingRatio}%`,
          '&:after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            opacity: 0,
            zIndex: theme.zIndices[2],
            ...loadingCSS(theme)
          }
        },
        isBackgroundImage && {
          '&::before': {
            display: 'block',
            content: '""',
            paddingTop: `${paddingRatio}%`
          }
        },
        !isLoaded && {
          '&::after': {
            opacity: 1
          }
        }
      ]}
      /* eslint-disable-next-line react/jsx-sort-props */
      className={className}
    >
      {!isBackgroundImage ? (
        <img
          ref={ref}
          alt={alt}
          css={(theme: LucidTheme) => ({
            label: 'tileImage',
            zIndex: theme.zIndices.z10
          })}
          onLoad={onLoad}
          src={src}
          title={title}
        />
      ) : (
        <div
          css={{
            label: 'tileBackgroundImage',
            backgroundImage: `url(${src})`,
            height: '100%',
            width: '100%',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundClip: 'content-box',
            position: 'absolute',
            top: 0,
            bottom: 0
          }}
        />
      )}
    </div>
  );
};

export { Image };
export default Image;
